import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * App-contributed UserMenu section slot.
 *
 * The avatar dropdown (`UserMenu`) is shell-owned chrome living in
 * `@clarix/layout`, but a remote may need to add a domain control to it — e.g.
 * the Support app's self-serve availability picker, which depends on
 * `@clarix/services-support` and must NOT be pulled into this shared layout
 * package. Mirroring {@link ./TopBarAppSlots}, the `UserMenu` renders a portal
 * *target* and the active app fills it with `<UserMenuPortal>` from inside its
 * own provider tree, so the control keeps its QueryClient / contexts.
 *
 * Unlike the TopBar (always mounted), the dropdown content mounts only while the
 * menu is open (Radix). So the target is registered by *ref* rather than a fixed
 * DOM id: it appears when the menu opens and clears when it closes, and the
 * portal follows automatically. This lazily mounts the app control — and its
 * data fetches — only while the menu is open.
 *
 * `@clarix/layout` is an MF singleton, so this module-level registry is shared
 * between the shell bundle and every remote. Only one app fills the slot at a
 * time (only the active app's tree is mounted).
 *
 * NOTE: portaled children keep the *caller's* React context, not the shell's
 * Radix menu context. Render self-contained controls (buttons, radios), not
 * `<DropdownMenuItem>`s — those would not wire up to the surrounding menu.
 */

let target: HTMLElement | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

/** Ref callback for the UserMenu's in-content target node (el on open, null on close). */
function setTarget(el: HTMLElement | null) {
  target = el;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): HTMLElement | null {
  return target;
}

/**
 * Rendered by `UserMenu` inside its dropdown content. Registers the live DOM
 * node so an app's `<UserMenuPortal>` can fill it; the ref clears the registry
 * when the menu closes.
 */
export function UserMenuSlotTarget() {
  return <div ref={setTarget} />;
}

/**
 * Fills the `UserMenu` section from inside the caller's React tree (keeps its
 * QueryClient / contexts via the portal). Renders nothing while the menu is
 * closed (no target).
 */
export function UserMenuPortal({ children }: { children: ReactNode }) {
  const node = useSyncExternalStore(subscribe, getSnapshot, () => null);
  return node ? createPortal(children, node) : null;
}
